/**
 * Which settings rows leave this device in a backup or a sync file.
 *
 * Most settings are the pilot's: units, time zone, the default aircraft, the
 * currency reminders. Those travel, so a second device reads the same way as
 * the first. Two kinds do not:
 *
 *  - **Bookkeeping about this device.** When it last backed up, what its
 *    startup check last found, which folder it syncs to (a folder handle means
 *    nothing to another browser, and a path on a Mac means nothing on
 *    Windows). Carried across, each would describe the wrong machine.
 *  - **API keys** (`sources.<source>.<field>`). A backup is a file a pilot
 *    emails themselves, and a sync file sits in a cloud drive. A key that
 *    spends money on the pilot's account does not belong in either, so it is
 *    entered once per device instead. `docs/PRIVACY.md` makes that promise
 *    and this function is what keeps it.
 */

const DEVICE_LOCAL_KEYS: ReadonlySet<string> = new Set([
  'backup.lastBackupAt',
  'autoImport.lastCheckedAt',
  'autoImport.lastRun',
]);

const DEVICE_LOCAL_PREFIXES: readonly string[] = ['sync.', 'device.'];

/** `sources.fr24.apiKey` is a credential; `sources.preferred` and `sources.relay` are not. */
export function isCredentialSetting(key: string): boolean {
  return key.startsWith('sources.') && key.split('.').length >= 3;
}

export function isDeviceLocalSetting(key: string): boolean {
  return DEVICE_LOCAL_KEYS.has(key) || DEVICE_LOCAL_PREFIXES.some((p) => key.startsWith(p));
}

/** Whether a settings row is written into, and read back from, a backup or sync file. */
export function isPortableSetting(key: string): boolean {
  return !isDeviceLocalSetting(key) && !isCredentialSetting(key);
}
